/**
 * A small rich-text editor for e-mail bodies (owner, 2026-09-30: bold,
 * italic and the other basics, without the line breaks collapsing).
 *
 * contenteditable + the browser's own formatting commands; the HTML it
 * reports is cleaned to the same short list of tags the server keeps
 * (Backend/email_designs.clean_rich): paragraphs, line breaks, bold, italic,
 * underline, strike, links, lists, quotes. Pasting brings plain text only,
 * so Word and web page styling never ride along. Parents insert
 * {placeholders} at the caret through the ref.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { URL_OK, bodyToEditorHtml } from './types';

export interface RichTextHandle { insert: (text: string) => void; focus: () => void }

const MAP: Record<string, string | null> = {
  P: 'p', DIV: 'p', H1: 'p', H2: 'p', H3: 'p', H4: 'p', H5: 'p', H6: 'p', BR: 'br',
  B: 'b', STRONG: 'b', I: 'i', EM: 'i', U: 'u', S: 's', STRIKE: 's', DEL: 's', A: 'a',
  UL: 'ul', OL: 'ol', LI: 'li', BLOCKQUOTE: 'blockquote',
};
const DROP = new Set(['SCRIPT', 'STYLE', 'HEAD', 'TITLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'NOSCRIPT']);
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escAttr = (s: string) => esc(s).replace(/"/g, '&quot;');

/** The editor's HTML reduced to the allowed tags, attributes dropped. */
export const cleanHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return esc(node.textContent || '');
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as HTMLElement;
    if (DROP.has(el.tagName)) return '';
    const inner = Array.from(el.childNodes).map(walk).join('');
    const t = MAP[el.tagName];
    if (!t) return inner;
    if (t === 'br') return '<br>';
    if (t === 'a') {
      const href = (el.getAttribute('href') || '').trim();
      return URL_OK.test(href) ? `<a href="${escAttr(href)}">${inner}</a>` : inner;
    }
    return `<${t}>${inner}</${t}>`;
  };
  // Loose text / inline formatting between blocks gets a paragraph of its own
  // (as the server's cleaner does), so every line has the same spacing.
  const BLOCK = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE']);
  const out: string[] = [];
  let run = '';
  const flush = () => { if (run.replace(/<br>/g, '').trim()) out.push(`<p>${run.replace(/^(?:<br>)+|(?:<br>)+$/g, '')}</p>`); run = ''; };
  Array.from(root.childNodes).forEach(n => {
    if (n.nodeType === Node.ELEMENT_NODE && BLOCK.has((n as HTMLElement).tagName)) { flush(); out.push(walk(n)); }
    else run += walk(n);
  });
  flush();
  return out.join('').replace(/<p>(?:\s|<br>)*<\/p>/g, '').trim();
};

const TOOLS: { cmd: string; label: React.ReactNode; title: string }[] = [
  { cmd: 'bold', label: <b>B</b>, title: 'Bold (Ctrl/⌘+B)' },
  { cmd: 'italic', label: <i>I</i>, title: 'Italic (Ctrl/⌘+I)' },
  { cmd: 'underline', label: <u>U</u>, title: 'Underline (Ctrl/⌘+U)' },
  { cmd: 'strikeThrough', label: <s>S</s>, title: 'Strike through' },
  { cmd: 'insertUnorderedList', label: '• List', title: 'Bulleted list' },
  { cmd: 'insertOrderedList', label: '1. List', title: 'Numbered list' },
];

export const RichText = forwardRef<RichTextHandle, {
  value: string; onChange: (html: string) => void; onFocus?: () => void;
  minHeight?: number; placeholder?: string;
  /** Offer an "HTML source" view (the write-from-scratch body). */
  allowSource?: boolean;
  /** Open in the HTML source (a whole document pasted or sent before: the
   *  editor view would keep only the simple tags). A change (another e-mail
   *  loaded) switches the view to match. */
  initialSource?: boolean;
}>(({ value, onChange, onFocus, minHeight = 110, placeholder, allowSource, initialSource }, ref) => {
  const el = useRef<HTMLDivElement>(null);
  const src = useRef<HTMLTextAreaElement>(null);
  const lastEmitted = useRef<string | null>(null);
  const saved = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState('https://');
  const [urlErr, setUrlErr] = useState('');
  const [source, setSource] = useState(!!(allowSource && initialSource));
  const [empty, setEmpty] = useState(!value);
  useEffect(() => {
    if (!allowSource) return;
    setSource(!!initialSource); lastEmitted.current = null;
  }, [allowSource, initialSource]);

  // A value from outside (a design loaded, a mode switch) replaces the DOM;
  // our own edits come back equal to what was emitted and leave the caret be.
  useEffect(() => {
    if (source || !el.current) return;
    if (value !== lastEmitted.current) {
      el.current.innerHTML = value || '';
      lastEmitted.current = value || '';
      setEmpty(!(el.current.textContent || '').trim());
    }
  }, [value, source]);

  const emit = () => {
    const h = cleanHtml(el.current?.innerHTML || '');
    lastEmitted.current = h;
    setEmpty(!(el.current?.textContent || '').trim());
    onChange(h);
  };
  const saveSel = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && el.current && el.current.contains(s.anchorNode)) saved.current = s.getRangeAt(0).cloneRange();
  };
  const restoreSel = () => {
    el.current?.focus();
    const s = window.getSelection();
    if (saved.current && s) { s.removeAllRanges(); s.addRange(saved.current); }
  };
  const run = (cmd: string, arg?: string) => {
    restoreSel();
    document.execCommand(cmd, false, arg);
    saveSel();
    emit();
  };

  useImperativeHandle(ref, () => ({
    insert: (text: string) => {
      if (source && src.current) {
        const t = src.current;
        const a = t.selectionStart ?? value.length;
        const b = t.selectionEnd ?? value.length;
        onChange(value.slice(0, a) + text + value.slice(b));
        requestAnimationFrame(() => { t.focus(); t.setSelectionRange(a + text.length, a + text.length); });
        return;
      }
      run('insertText', text);
    },
    focus: () => (source ? src.current?.focus() : el.current?.focus()),
  }));

  const applyLink = () => {
    const u = url.trim();
    if (!URL_OK.test(u)) { setUrlErr('Start with https://, http://, mailto: or a placeholder like {upgrade_url}'); return; }
    setUrlErr('');
    setLinkOpen(false);
    const s = saved.current;
    if (s && s.collapsed) {
      // No text selected: the address becomes the link text.
      run('insertHTML', `<a href="${escAttr(u)}">${esc(u)}</a>`);
    } else {
      run('createLink', u);
    }
  };

  const btn = 'h-7 min-w-[28px] px-1.5 rounded text-xs text-grayscale-1200 hover:bg-grayscale-200 border border-transparent';
  return (
    <div className="rounded-md border-[0.5px] border-primary-300 bg-white">
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-grayscale-200 bg-grayscale-200/40">
        {!source && TOOLS.map(t => (
          <button key={t.cmd} type="button" title={t.title} className={btn}
            onMouseDown={e => { e.preventDefault(); run(t.cmd); }}>{t.label}</button>
        ))}
        {!source && (
          <>
            <button type="button" title="Link" className={btn}
              onMouseDown={e => { e.preventDefault(); saveSel(); setLinkOpen(o => !o); setUrlErr(''); }}>Link</button>
            <button type="button" title="Remove link" className={btn}
              onMouseDown={e => { e.preventDefault(); run('unlink'); }}>Unlink</button>
            <button type="button" title="Clear formatting on the selection" className={btn}
              onMouseDown={e => { e.preventDefault(); run('removeFormat'); }}>Clear</button>
          </>
        )}
        {allowSource && (
          <button type="button" className={`${btn} ml-auto ${source ? 'bg-primary-100 border-primary-300' : ''}`}
            title="Edit the HTML directly"
            onClick={() => {
              // Back from the source: text with only inline tags gets its
              // paragraphs and line breaks, or the editor would run it together.
              if (source) { const v = bodyToEditorHtml(value); if (v !== value) onChange(v); }
              setSource(s => !s); lastEmitted.current = null;
            }}>
            {source ? 'Back to editor' : 'HTML source'}
          </button>
        )}
      </div>
      {linkOpen && !source && (
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border-b border-grayscale-200 bg-primary-100/40">
          <input autoFocus value={url} onChange={e => setUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } if (e.key === 'Escape') setLinkOpen(false); }}
            placeholder="https://… or {upgrade_url}" className="h-7 px-2 rounded border border-primary-300 text-xs flex-1 min-w-[12rem]" />
          <button type="button" className="h-7 px-2.5 rounded bg-primary-500 text-grayscale-100 text-xs" onClick={applyLink}>Add link</button>
          <button type="button" className="h-7 px-2 rounded text-xs" onClick={() => setLinkOpen(false)}>Cancel</button>
          {urlErr && <span className="w-full text-[11px] text-red-700">{urlErr}</span>}
        </div>
      )}
      {source ? (
        <textarea ref={src} value={value} onChange={e => onChange(e.target.value)} onFocus={onFocus}
          rows={Math.max(6, Math.round(minHeight / 20))}
          className="w-full px-3 py-2 text-xs font-mono text-grayscale-1300 bg-white rounded-b-md outline-none" />
      ) : (
        <div className="relative">
          {empty && placeholder && (
            <div className="absolute left-3 top-2 text-sm text-grayscale-1100 pointer-events-none select-none">{placeholder}</div>
          )}
          <div ref={el} contentEditable suppressContentEditableWarning
            onInput={emit} onFocus={onFocus} onBlur={saveSel} onKeyUp={saveSel} onMouseUp={saveSel}
            onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
            className="px-3 py-2 text-sm text-grayscale-1300 outline-none [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:underline [&_a]:text-primary-700"
            style={{ minHeight }} />
        </div>
      )}
    </div>
  );
});
RichText.displayName = 'RichText';

export default RichText;
